import { useEffect, useMemo, useState } from 'react';
import MissingFieldsModal from './MissingFieldsModal';
import './ZZPShared.css';
import './ContractFormModal.css';
import './BulkContractModal.css';
import { fold } from '../../../utils/zoek';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  return fetch(url, { ...options, headers, credentials: 'include' });
}

const EMPTY_FORM = {
  opdrachtgeverId: '',
  project_naam: '',
  werkzaamheden: '',
  datum_in: '',
  datum_uit: '',
  tekendatum: '',
};

export default function BulkContractModal({ onClose, onSaved }) {
  const [workers, setWorkers]               = useState([]);
  const [opdrachtgevers, setOpdrachtgevers] = useState([]);
  const [werkzaamheden, setWerkzaamheden]   = useState([]);
  const [functiegroepen, setFunctiegroepen] = useState([]);

  const [selectedWorkerIds, setSelectedWorkerIds] = useState(() => new Set());
  const [workerSearch, setWorkerSearch] = useState('');

  const [werkzaamhedenMode, setWerkzaamhedenMode] = useState('select'); // 'select' | 'other'

  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [missingPerWorker, setMissingPerWorker] = useState(null); // [{worker_id, worker_naam, missing:[]}] | null

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [wRes, kRes, oRes, fRes] = await Promise.all([
          authFetch('/api/workers'),
          authFetch('/api/zzp/werkzaamheden'),
          authFetch('/api/zzp/opdrachtgevers'),
          authFetch('/api/zzp/functiegroepen'),
        ]);
        const wData = await wRes.json().catch(() => []);
        const kData = await kRes.json().catch(() => []);
        const oData = await oRes.json().catch(() => []);
        const fData = await fRes.json().catch(() => []);
        if (cancelled) return;
        setWorkers(Array.isArray(wData) ? wData : []);
        setWerkzaamheden(Array.isArray(kData) ? kData : []);
        setFunctiegroepen(Array.isArray(fData) ? fData : []);
        const oList = Array.isArray(oData) ? oData : [];
        setOpdrachtgevers(oList);
        if (oList.length > 0) {
          setForm((p) => p.opdrachtgeverId ? p : { ...p, opdrachtgeverId: String(oList[0].id) });
        }
      } catch {
        if (!cancelled) {
          setWorkers([]); setOpdrachtgevers([]); setWerkzaamheden([]); setFunctiegroepen([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const workerOptions = useMemo(() => workers.map((w) => {
    const naam = `${w.firstName || ''} ${w.lastName || ''}`.trim() || w.name || `Worker #${w.id}`;
    const bedrijf = w.bedrijfsnaam ? ` — ${w.bedrijfsnaam}` : '';
    return { id: w.id, label: naam + bedrijf, naam, functiegroepId: w.functiegroepId || null };
  }), [workers]);

  const filteredWorkers = useMemo(() => {
    const q = fold(workerSearch).trim();
    if (!q) return workerOptions;
    return workerOptions.filter((o) => fold(o.label).includes(q));
  }, [workerOptions, workerSearch]);

  const selectedWorkersSummary = useMemo(() => {
    return workerOptions
      .filter((w) => selectedWorkerIds.has(w.id))
      .map((w) => {
        const fg = w.functiegroepId
          ? functiegroepen.find((f) => String(f.id) === String(w.functiegroepId))
          : null;
        return {
          id: w.id,
          naam: w.naam,
          functiegroep: fg || null,
        };
      });
  }, [workerOptions, selectedWorkerIds, functiegroepen]);

  const toggleWorker = (id) => {
    setSelectedWorkerIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelectedWorkerIds((prev) => {
      const next = new Set(prev);
      filteredWorkers.forEach((w) => next.add(w.id));
      return next;
    });
  };

  const clearAll = () => setSelectedWorkerIds(new Set());

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    setError('');
  };

  const handleWerkzaamhedenSelectChange = (e) => {
    const value = e.target.value;
    if (value === '__other__') { setWerkzaamhedenMode('other'); return; }
    setWerkzaamhedenMode('select');
    setForm((p) => ({ ...p, werkzaamheden: value }));
    setError('');
  };

  const werkzaamhedenLabels = werkzaamheden.map((w) => w.label);
  const werkzaamhedenSelectValue =
    werkzaamhedenMode === 'other'
      ? '__other__'
      : (werkzaamhedenLabels.includes(form.werkzaamheden) ? form.werkzaamheden : '');

  const buildPayload = () => {
    return {
      worker_ids:       Array.from(selectedWorkerIds),
      opdrachtgever_id: form.opdrachtgeverId ? parseInt(form.opdrachtgeverId, 10) : null,
      project_naam:     form.project_naam.trim(),
      werkzaamheden:    form.werkzaamheden.trim(),
      datum_in:         form.datum_in   || null,
      datum_uit:         form.datum_uit  || null,
      tekendatum:       form.tekendatum || null,
    };
  };

  const downloadZip = async (res) => {
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = res.headers.get('Content-Disposition') || '';
    const m  = cd.match(/filename="?([^"]+)"?/);
    a.download = m ? m[1] : 'contracten.zip';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const submitBulk = async () => {
    if (selectedWorkerIds.size === 0) {
      setError('Selecteer minimaal één medewerker');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = buildPayload();
      const res = await authFetch('/api/zzp/contracts/bulk-generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        await downloadZip(res);
        if (onSaved) onSaved();
        return;
      }

      // Validation failure: 400 with details — show modal, block submission
      if (res.status === 400) {
        const data = await res.json().catch(() => ({}));
        const details = Array.isArray(data?.details) ? data.details : [];
        if (details.length) {
          setMissingPerWorker(details);
          setSaving(false);
          return;
        }
        setError(data?.error || 'Validatie mislukt');
        setSaving(false);
        return;
      }

      const data = await res.json().catch(() => ({}));
      setError(data?.error || 'Genereren mislukt');
    } catch (err) {
      setError(err.message || 'Genereren mislukt');
    }
    setSaving(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitBulk(false);
  };

  return (
    <div className="zzp-modal-overlay" onClick={onClose}>
      <div className="zzp-modal-content cfm-modal bulk-modal" onClick={(e) => e.stopPropagation()}>
        <div className="zzp-modal-header">
          <h3>Bulk contracten genereren</h3>
          <button className="zzp-modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="zzp-form">
          <div className="zzp-form-group">
            <div className="bulk-workers-header">
              <label>Workers ({selectedWorkerIds.size} geselecteerd)</label>
              <div className="bulk-workers-actions">
                <button type="button" className="bulk-mini-btn" onClick={selectAllFiltered}>Alles in lijst</button>
                <button type="button" className="bulk-mini-btn" onClick={clearAll}>Wissen</button>
              </div>
            </div>
            <input
              type="text"
              className="bulk-worker-search"
              placeholder="Zoek worker..."
              value={workerSearch}
              onChange={(e) => setWorkerSearch(e.target.value)}
            />
            <div className="bulk-worker-list">
              {filteredWorkers.length === 0 ? (
                <div className="bulk-worker-empty">Geen workers gevonden</div>
              ) : (
                filteredWorkers.map((w) => (
                  <label key={w.id} className="bulk-worker-row">
                    <input
                      type="checkbox"
                      checked={selectedWorkerIds.has(w.id)}
                      onChange={() => toggleWorker(w.id)}
                    />
                    <span>{w.label}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          {selectedWorkersSummary.length > 0 && (
            <div className="zzp-form-group">
              <div className="bulk-functiegroep-summary">
                <div className="bulk-functiegroep-summary-title">
                  Gekoppelde tarieven per medewerker:
                </div>
                <ul className="bulk-functiegroep-summary-list">
                  {selectedWorkersSummary.map((w) => (
                    <li key={w.id}>
                      <span className="bulk-fg-naam">{w.naam}</span>
                      {w.functiegroep ? (
                        <span className="bulk-fg-value">— Functiegroep {w.functiegroep.id} ({w.functiegroep.naam})</span>
                      ) : (
                        <span className="bulk-fg-warning">— ⚠️ Geen functiegroep ingesteld</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="zzp-form-group">
            <label htmlFor="opdrachtgeverId">Opdrachtgever</label>
            <select
              id="opdrachtgeverId"
              name="opdrachtgeverId"
              value={form.opdrachtgeverId}
              onChange={handleChange}
            >
              <option value="">— Kies opdrachtgever —</option>
              {opdrachtgevers.map((o) => (
                <option key={o.id} value={o.id}>{o.naam}</option>
              ))}
            </select>
          </div>

          <div className="zzp-form-group">
            <label htmlFor="project_naam">Project naam</label>
            <input
              id="project_naam"
              type="text"
              name="project_naam"
              placeholder="Project Keppel Verolme"
              value={form.project_naam}
              onChange={handleChange}
            />
          </div>

          <div className="zzp-form-group">
            <label htmlFor="bulk_werkzaamheden_select">Werkzaamheden</label>
            <select
              id="bulk_werkzaamheden_select"
              value={werkzaamhedenSelectValue}
              onChange={handleWerkzaamhedenSelectChange}
            >
              <option value="">— Kies werkzaamheden —</option>
              {werkzaamheden.map((w) => (
                <option key={w.id} value={w.label}>{w.label}</option>
              ))}
              <option value="__other__">Anders…</option>
            </select>
            {werkzaamhedenMode === 'other' && (
              <textarea
                name="werkzaamheden"
                rows={4}
                placeholder="Omschrijving van de werkzaamheden..."
                value={form.werkzaamheden}
                onChange={handleChange}
                style={{ marginTop: 8 }}
              />
            )}
          </div>

          <div className="zzp-form-row cfm-row-3">
            <div className="zzp-form-group">
              <label htmlFor="datum_in">Datum in</label>
              <input id="datum_in" type="date" name="datum_in" value={form.datum_in} onChange={handleChange} />
            </div>
            <div className="zzp-form-group">
              <label htmlFor="datum_uit">Datum uit</label>
              <input id="datum_uit" type="date" name="datum_uit" value={form.datum_uit} onChange={handleChange} />
            </div>
            <div className="zzp-form-group">
              <label htmlFor="tekendatum">Tekendatum</label>
              <input id="tekendatum" type="date" name="tekendatum" value={form.tekendatum} onChange={handleChange} />
            </div>
          </div>

          {error && <div className="zzp-form-error">{error}</div>}

          <div className="zzp-form-actions">
            <button type="button" className="zzp-btn-cancel" onClick={onClose}>
              Annuleren
            </button>
            <button type="submit" className="zzp-btn-submit" disabled={saving || selectedWorkerIds.size === 0}>
              {saving ? 'Genereren...' : `Genereer ZIP (${selectedWorkerIds.size})`}
            </button>
          </div>
        </form>
      </div>

      <MissingFieldsModal
        open={!!missingPerWorker}
        onClose={() => setMissingPerWorker(null)}
        perWorker={missingPerWorker || []}
        title="Bulk genereren geblokkeerd"
      />
    </div>
  );
}
